import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import {
  WORKSPACE_CN,
  WORKSPACE_STORAGE_KEY,
  canUseWorkspace,
  normalizeDistributeIdentity,
  readStoredWorkspace,
  resolveWorkspaceAfterBootstrap,
  resolveWorkspaceAfterSwitch,
  resolveWorkspaceOnRefresh,
} from "../utils/workspace";
import {
  registerWorkspaceController,
  unregisterWorkspaceController,
} from "../utils/workspaceSync";

const WorkspaceContext = createContext(null);

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceRegionProvider");
  }
  return context;
}

const WorkspaceRegionProvider = ({ children }) => {
  const permission = useSelector((state) => state.permission);
  const distributeIdentity = normalizeDistributeIdentity(
    permission.currentIdentity?.distributeIdentity
  );
  const [workspace, setWorkspaceState] = useState(
    () => readStoredWorkspace() || WORKSPACE_CN
  );

  const persistWorkspace = useCallback((nextWorkspace) => {
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, nextWorkspace);
    } catch (error) {
      // ignore quota / private mode
    }
  }, []);

  const setWorkspace = useCallback(
    (nextWorkspace) => {
      const resolved = canUseWorkspace(distributeIdentity, nextWorkspace)
        ? nextWorkspace
        : WORKSPACE_CN;
      setWorkspaceState(resolved);
      persistWorkspace(resolved);
    },
    [distributeIdentity, persistWorkspace]
  );

  const applyBootstrap = useCallback(
    (accessInfo) => {
      const nextWorkspace = resolveWorkspaceAfterBootstrap(accessInfo);
      setWorkspaceState(nextWorkspace);
      persistWorkspace(nextWorkspace);
    },
    [persistWorkspace]
  );

  const applySwitch = useCallback(
    (accessInfo) => {
      const nextWorkspace = resolveWorkspaceAfterSwitch(accessInfo);
      setWorkspaceState(nextWorkspace);
      persistWorkspace(nextWorkspace);
    },
    [persistWorkspace]
  );

  useEffect(() => {
    registerWorkspaceController({ applyBootstrap, applySwitch });
    return () => unregisterWorkspaceController();
  }, [applyBootstrap, applySwitch]);

  useEffect(() => {
    if (!permission.initialized || permission.needSelectIdentity) {
      return;
    }

    const nextWorkspace = resolveWorkspaceOnRefresh(
      permission.currentIdentity?.distributeIdentity,
      readStoredWorkspace()
    );
    setWorkspaceState(nextWorkspace);
    persistWorkspace(nextWorkspace);
  }, [
    permission.initialized,
    permission.needSelectIdentity,
    permission.currentIdentity?.identityId,
    permission.currentIdentity?.distributeIdentity,
    persistWorkspace,
  ]);

  const value = useMemo(
    () => ({
      workspace,
      setWorkspace,
      distributeIdentity,
      canUseIntl: canUseWorkspace(distributeIdentity, "intl"),
      showWorkspaceSwitcher: distributeIdentity === 2,
    }),
    [workspace, setWorkspace, distributeIdentity]
  );

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  );
};

export default WorkspaceRegionProvider;
